/* eslint-disable i18next/no-literal-string --
 * Seed vocabulary, not UI copy. These value/code pairs, field names and
 * category names are written to the database when an org adds the standard
 * fields, and the codes are emitted into generated campaign names. A
 * translated 'North America' would put a localized segment into an
 * ad-server name and break the naming standard it exists to enforce.
 */
import {
  BadgePercent,
  Calendar,
  DollarSign,
  Flag,
  Frame,
  Globe,
  Image,
  Languages,
  Layers,
  LayoutTemplate,
  Lightbulb,
  Megaphone,
  MonitorSmartphone,
  MousePointerClick,
  Package,
  Radio,
  ShoppingBag,
  Smartphone,
  Target,
  Users,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import type {
  TaxonomyFieldGroup,
  TaxonomyFieldType,
  TaxonomyFieldValue,
} from '@/entities/taxonomy';
import { STARTER_CATEGORY_ICON } from '@/entities/taxonomy-category';

/**
 * A ready-to-use marketing field from the standard library. Mirrors the shape
 * of a schema field (name/group/type/values + optional date `format` mask) so
 * it can be dropped straight into the builder. Grounded in the common
 * marketing-taxonomy conventions (Claravine / CM360 / brand naming standards):
 * controlled value lists with short output codes, split into media vs content.
 */
export interface StandardField {
  /** Stable catalog key (dedupe / React key). */
  key: string;
  category: string;
  name: string;
  group: TaxonomyFieldGroup;
  type: TaxonomyFieldType;
  /** Date mask for `type: 'date'` (e.g. "YYYY", "MMDD"). */
  format?: string;
  values: TaxonomyFieldValue[];
}

/**
 * Per-category icon for the picker's left rail.
 *
 * Re-exported from the category entity rather than declared here: the same five
 * categories are seeded into the registry with these icons, and keeping one
 * mapping means a rail icon and a seeded row can't disagree.
 */
export const CATEGORY_ICON: Record<string, LucideIcon> = STARTER_CATEGORY_ICON;

/** Per-field icon for the picker cards (keyed by StandardField.key). */
export const FIELD_ICON: Record<string, LucideIcon> = {
  region: Globe,
  country: Flag,
  language: Languages,
  channel: Radio,
  platform: MonitorSmartphone,
  'buy-type': DollarSign,
  'buy-method': ShoppingBag,
  device: Smartphone,
  objective: Target,
  'funnel-stage': Megaphone,
  'funding-source': Wallet,
  'flight-year': Calendar,
  'flight-start': Calendar,
  'flight-end': Calendar,
  'creative-concept': Lightbulb,
  'creative-type': Image,
  'ad-format': Frame,
  placement: LayoutTemplate,
  'ad-type': Megaphone,
  audience: Users,
  cta: MousePointerClick,
  product: Package,
  offer: BadgePercent,
  version: Layers,
};

const v = (value: string, code: string): TaxonomyFieldValue => ({ value, code });

export const STANDARD_FIELD_LIBRARY: StandardField[] = [
  // ─────────────── Geography ───────────────
  {
    key: 'region',
    category: 'Geography',
    name: 'Region',
    group: 'media',
    type: 'dropdown',
    values: [
      v('North America', 'NA'),
      v('EMEA', 'EM'),
      v('APAC', 'AP'),
      v('LATAM', 'LA'),
      v('Global', 'GL'),
    ],
  },
  {
    key: 'country',
    category: 'Geography',
    name: 'Country',
    group: 'media',
    type: 'dropdown',
    values: [
      v('United States', 'US'),
      v('United Kingdom', 'GB'),
      v('Canada', 'CA'),
      v('Germany', 'DE'),
      v('France', 'FR'),
      v('Japan', 'JP'),
      v('Australia', 'AU'),
      v('Brazil', 'BR'),
    ],
  },
  {
    key: 'language',
    category: 'Geography',
    name: 'Language',
    group: 'media',
    type: 'dropdown',
    values: [
      v('English', 'EN'),
      v('Spanish', 'ES'),
      v('French', 'FR'),
      v('German', 'DE'),
      v('Japanese', 'JA'),
      v('Portuguese', 'PT'),
    ],
  },

  // ─────────────── Media buy ───────────────
  {
    key: 'channel',
    category: 'Media buy',
    name: 'Channel',
    group: 'media',
    type: 'dropdown',
    values: [
      v('Paid Social', 'PS'),
      v('Paid Search', 'SEM'),
      v('Display', 'DIS'),
      v('Online Video', 'OLV'),
      v('Connected TV', 'CTV'),
      v('Audio', 'AUD'),
      v('Out of Home', 'OOH'),
      v('Email', 'EM'),
    ],
  },
  {
    key: 'platform',
    category: 'Media buy',
    name: 'Platform',
    group: 'media',
    type: 'dropdown',
    values: [
      v('Meta', 'META'),
      v('TikTok', 'TT'),
      v('Google Ads', 'GADS'),
      v('DV360', 'DV3'),
      v('YouTube', 'YT'),
      v('LinkedIn', 'LI'),
      v('Amazon', 'AMZ'),
      v('Pinterest', 'PIN'),
      v('Reddit', 'RDT'),
    ],
  },
  {
    key: 'buy-type',
    category: 'Media buy',
    name: 'Buy Type',
    group: 'media',
    type: 'dropdown',
    values: [
      v('CPM', 'CPM'),
      v('CPC', 'CPC'),
      v('CPV', 'CPV'),
      v('CPA', 'CPA'),
      v('CPCV', 'CPCV'),
      v('Flat', 'FLAT'),
    ],
  },
  {
    key: 'buy-method',
    category: 'Media buy',
    name: 'Buy Method',
    group: 'media',
    type: 'dropdown',
    values: [
      v('Programmatic', 'PROG'),
      v('Direct / IO', 'IO'),
      v('PMP', 'PMP'),
      v('Managed', 'MGD'),
    ],
  },
  {
    key: 'device',
    category: 'Media buy',
    name: 'Device',
    group: 'media',
    type: 'dropdown',
    values: [v('Desktop', 'DSK'), v('Mobile', 'MOB'), v('Tablet', 'TAB'), v('Connected TV', 'CTV')],
  },

  // ─────────────── Campaign ───────────────
  {
    key: 'objective',
    category: 'Campaign',
    name: 'Objective',
    group: 'media',
    type: 'dropdown',
    values: [
      v('Awareness', 'AWA'),
      v('Consideration', 'CON'),
      v('Conversion', 'CNV'),
      v('Retention', 'RET'),
    ],
  },
  {
    key: 'funnel-stage',
    category: 'Campaign',
    name: 'Funnel Stage',
    group: 'media',
    type: 'dropdown',
    values: [v('Upper', 'UF'), v('Mid', 'MF'), v('Lower', 'LF')],
  },
  {
    key: 'funding-source',
    category: 'Campaign',
    name: 'Funding Source',
    group: 'media',
    type: 'dropdown',
    values: [v('Brand', 'BR'), v('Co-op', 'CO'), v('Trade', 'TR')],
  },
  {
    key: 'flight-year',
    category: 'Campaign',
    name: 'Flight Year',
    group: 'media',
    type: 'date',
    format: 'YYYY',
    values: [],
  },
  {
    key: 'flight-start',
    category: 'Campaign',
    name: 'Flight Start Date',
    group: 'media',
    type: 'date',
    format: 'YYYYMMDD',
    values: [],
  },
  {
    key: 'flight-end',
    category: 'Campaign',
    name: 'Flight End Date',
    group: 'media',
    type: 'date',
    format: 'YYYYMMDD',
    values: [],
  },

  // ─────────────── Creative ───────────────
  {
    key: 'creative-concept',
    category: 'Creative',
    name: 'Creative Concept',
    group: 'content',
    type: 'free_text',
    values: [],
  },
  {
    key: 'creative-type',
    category: 'Creative',
    name: 'Creative Type',
    group: 'content',
    type: 'dropdown',
    values: [
      v('Static', 'STA'),
      v('Video', 'VID'),
      v('Carousel', 'CAR'),
      v('GIF', 'GIF'),
      v('HTML5', 'H5'),
    ],
  },
  {
    key: 'ad-format',
    category: 'Creative',
    name: 'Ad Format',
    group: 'content',
    type: 'dropdown',
    // Vocabulary is governed by the `ad-formats` standard list (see standard-field-lists.ts).
    // An inline fallback here could only ever contradict it, since the governed rows carry
    // platform-specific format codes that this two-tuple shape can't express.
    values: [],
  },
  {
    key: 'placement',
    category: 'Creative',
    name: 'Placement',
    group: 'content',
    type: 'dropdown',
    values: [
      v('Feed', 'FEED'),
      v('Story', 'STORY'),
      v('Reel', 'REEL'),
      v('In-stream', 'INSTR'),
      v('Native', 'NAT'),
    ],
  },
  {
    key: 'ad-type',
    category: 'Creative',
    name: 'Ad Type',
    group: 'content',
    type: 'dropdown',
    values: [v('Standard', 'STD'), v('Collection', 'COL'), v('Lead', 'LEAD')],
  },

  // ─────────────── Targeting & offer ───────────────
  {
    key: 'audience',
    category: 'Targeting & offer',
    name: 'Audience',
    group: 'content',
    type: 'dropdown',
    values: [
      v('Prospecting', 'PRO'),
      v('Retargeting', 'RET'),
      v('Lookalike', 'LAL'),
      v('CRM', 'CRM'),
    ],
  },
  {
    key: 'cta',
    category: 'Targeting & offer',
    name: 'CTA',
    group: 'content',
    type: 'dropdown',
    values: [
      v('Shop Now', 'SHOP'),
      v('Learn More', 'LEARN'),
      v('Sign Up', 'SIGN'),
      v('Download', 'DL'),
    ],
  },
  {
    key: 'product',
    category: 'Targeting & offer',
    name: 'Product',
    group: 'content',
    type: 'free_text',
    values: [],
  },
  {
    key: 'offer',
    category: 'Targeting & offer',
    name: 'Offer',
    group: 'content',
    type: 'free_text',
    values: [],
  },
  {
    key: 'version',
    category: 'Targeting & offer',
    name: 'Version',
    group: 'content',
    type: 'dropdown',
    values: [v('Version 1', 'V1'), v('Version 2', 'V2'), v('Variant A', 'A'), v('Variant B', 'B')],
  },
];

/** Catalog grouped by category, preserving declaration order. */
export const STANDARD_FIELD_CATEGORIES: { category: string; fields: StandardField[] }[] =
  STANDARD_FIELD_LIBRARY.reduce<{ category: string; fields: StandardField[] }[]>((acc, field) => {
    const bucket = acc.find((b) => b.category === field.category);
    if (bucket) bucket.fields.push(field);
    else acc.push({ category: field.category, fields: [field] });
    return acc;
  }, []);

const STANDARD_FIELD_BY_KEY = new Map(STANDARD_FIELD_LIBRARY.map((f) => [f.key, f]));

/** Look up a catalog entry by its `key` — powers "Reset to default" on library fields. */
export function getStandardFieldByKey(key: string | null | undefined): StandardField | undefined {
  return key ? STANDARD_FIELD_BY_KEY.get(key) : undefined;
}

/**
 * Sensible default campaign-hierarchy level for each library field, so pulling a
 * standard field pre-fills the SME "guardrail" (users can still override). Global
 * fields default to `all`; anything not listed falls back to `all`.
 */
const LEVEL_BY_KEY: Record<string, string> = {
  region: 'all',
  country: 'all',
  language: 'all',
  objective: 'campaign',
  'funnel-stage': 'campaign',
  'funding-source': 'campaign',
  'flight-year': 'campaign',
  'flight-start': 'campaign',
  'flight-end': 'campaign',
  channel: 'ad_set',
  platform: 'ad_set',
  'buy-type': 'ad_set',
  'buy-method': 'ad_set',
  device: 'ad_set',
  audience: 'ad_set',
  'creative-concept': 'ad',
  'creative-type': 'ad',
  'ad-format': 'ad',
  placement: 'ad',
  'ad-type': 'ad',
  cta: 'ad',
  product: 'ad',
  offer: 'ad',
  version: 'ad',
};

/** Default hierarchy level for a standard field (falls back to `all`). */
export function standardFieldLevel(key: string | null | undefined): string {
  return (key && LEVEL_BY_KEY[key]) || 'all';
}
